import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, TextInput, ActionIcon, Checkbox, Group, Button as MantineButton, Select, Switch } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark as faXmarkRemoveDef, faArrowsUpDown as faArrowsUpDownDef } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { CategoryView } from '../../../../../../services/categories/categories.service';
import type { ProductView } from '../../../../../../services/products/products.service';
import { Button } from '../../../../../../components/Button';
import navyModal from '../../../../../../components/Modal/navy-modal.module.scss';
import { useCategoryManagerController } from './useCategoryManagerController';
import { CategoryManagerRow } from './CategoryManagerRow';
import { SortableCategoryRow } from './SortableCategoryRow';
import { CategoryMembershipDrawer } from '../category-membership-drawer/CategoryMembershipDrawer';
import { QuickNotesField } from '../quick-notes/QuickNotesField';
import { depthOf } from '../shared/category-manager.utils';
import shared from '../../shared/settings-shared.module.scss';
import productClasses from '../../shared/product-settings.module.scss';

const faXmarkRemove = faXmarkRemoveDef as IconProp;
const faArrowsUpDown = faArrowsUpDownDef as IconProp;

const ROW_ESTIMATED_HEIGHT = 57;
const VIRTUALIZE_THRESHOLD = 16;
const CATEGORY_COLUMN_COUNT = 7;

export type CategoryManagerModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
  categories: CategoryView[];
  products: ProductView[];
};

const CategoryManagerModalComponent: React.FC<CategoryManagerModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  categories,
  products,
}) => {
  const ctrl = useCategoryManagerController({ tenantId, employeeSession, categories, products });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [reorderMode, setReorderMode] = useState(false);
  const isCompact = useMediaQuery('(max-width: 768px)', false);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  // Defer the (windowed) row body one paint after open so the modal shell/toolbar feels instant.
  const [bodyMounted, setBodyMounted] = useState(false);
  useEffect(() => {
    setBodyMounted(opened);
  }, [opened]);
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    void ctrl.reorderCategory(Number(active.id), Number(over.id));
  };
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const sortableItems = useMemo(() => ctrl.orderedCategories.map((category) => category.id), [ctrl.orderedCategories]);

  // Windowing is desktop/tablet only; mobile card mode renders the full (bounded) list.
  const shouldVirtualize = bodyMounted && !reorderMode && !isCompact && ctrl.orderedCategories.length > VIRTUALIZE_THRESHOLD;
  const rowVirtualizer = useVirtualizer({
    count: ctrl.orderedCategories.length,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => ROW_ESTIMATED_HEIGHT,
    overscan: 10,
    getItemKey: (index) => ctrl.orderedCategories[index]?.id ?? index,
    enabled: shouldVirtualize,
  });
  const virtualItems = shouldVirtualize ? rowVirtualizer.getVirtualItems() : [];
  const virtualPaddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const virtualPaddingBottom =
    virtualItems.length > 0 ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end : 0;

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const isFormModalOpen = isCreateModalOpen || ctrl.editingId !== null;
  const isEditMode = ctrl.editingId !== null;
  const editingCategory = ctrl.editingId !== null ? categoryById.get(ctrl.editingId) ?? null : null;

  const handleClose = () => {
    if (!ctrl.isAnyWriteInFlight) {
      onClose();
    }
  };

  const closeFormModal = () => {
    if (ctrl.isAnyWriteInFlight) return;
    setCreateModalOpen(false);
    ctrl.cancelEdit();
  };

  // isCreateModalOpen is local state — close it here on the same success signal
  // cancelEdit() already uses (rows collapsed back to one empty row).
  const wasSavingFormRef = useRef(false);
  useEffect(() => {
    if (wasSavingFormRef.current && !ctrl.isSavingForm && ctrl.rows.length === 1 && ctrl.rows[0].name === '' && isCreateModalOpen) {
      setCreateModalOpen(false);
    }
    wasSavingFormRef.current = ctrl.isSavingForm;
  }, [ctrl.isSavingForm, ctrl.rows, isCreateModalOpen]);

  const categoryRowData = (category: CategoryView) => ({
    depthPrefix: '↳ '.repeat(depthOf(category.id, categoryById) - 1),
    parentOptions: ctrl.parentOptionsByCategory.get(category.id) ?? [],
    productCount: ctrl.productCountByCategory.get(category.id) ?? 0,
    isWriteInFlight: ctrl.isAnyWriteInFlight,
    isToggling: ctrl.togglingId === category.id,
    onStartEdit: ctrl.startEdit,
    onSetParent: ctrl.setCategoryParent,
    onSetReportGroup: ctrl.setCategoryReportGroup,
    onOpenMembership: ctrl.setMembershipCategoryId,
    onToggleActive: ctrl.toggleActive,
  });

  const renderCategoryRow = (category: CategoryView) => (
    <CategoryManagerRow key={category.id} category={category} reorderMode={false} {...categoryRowData(category)} />
  );

  const renderSortableCategoryRow = (category: CategoryView) => (
    <SortableCategoryRow key={category.id} category={category} {...categoryRowData(category)} />
  );

  const emptyStateRow =
    ctrl.orderedCategories.length === 0 ? (
      <tr>
        <td colSpan={CATEGORY_COLUMN_COUNT} className={shared.empty}>
          לא הוגדרו קטגוריות עדיין.
        </td>
      </tr>
    ) : null;

  const renderCategoryTable = (reorder: boolean) => (
    <table className={`${shared.table} ${productClasses.cardTable}`}>
      <thead>
        <tr>
          <th className={productClasses.dragCell} aria-hidden />
          <th>שם</th>
          <th>קטגוריית אם</th>
          <th>אוכל</th>
          <th>משקאות</th>
          <th>סטטוס</th>
          <th>פעולות</th>
        </tr>
      </thead>
      {reorder ? (
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          <tbody>
            {ctrl.orderedCategories.map((category) => renderSortableCategoryRow(category))}
            {emptyStateRow}
          </tbody>
        </SortableContext>
      ) : (
        <tbody>
          {!bodyMounted && ctrl.orderedCategories.length > 0 ? (
            <tr>
              <td colSpan={CATEGORY_COLUMN_COUNT} className={shared.empty}>טוען קטגוריות...</td>
            </tr>
          ) : shouldVirtualize ? (
            <>
              {virtualPaddingTop > 0 && (
                <tr aria-hidden className={productClasses.virtualSpacerRow}>
                  <td colSpan={CATEGORY_COLUMN_COUNT} style={{ height: virtualPaddingTop, padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualItems.map((virtualItem) =>
                renderCategoryRow(ctrl.orderedCategories[virtualItem.index]),
              )}
              {virtualPaddingBottom > 0 && (
                <tr aria-hidden className={productClasses.virtualSpacerRow}>
                  <td colSpan={CATEGORY_COLUMN_COUNT} style={{ height: virtualPaddingBottom, padding: 0, border: 0 }} />
                </tr>
              )}
            </>
          ) : (
            ctrl.orderedCategories.map((category) => renderCategoryRow(category))
          )}
          {bodyMounted && emptyStateRow}
        </tbody>
      )}
    </table>
  );

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        onContextMenu={(event) => event.preventDefault()}
        title="ניהול קטגוריות"
        size="xl"
        centered
        radius="md"
        classNames={{ root: navyModal.root, body: shared.narrowBody }}
        closeButtonProps={{ disabled: ctrl.isAnyWriteInFlight, 'data-testid': 'category-manager-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
        closeOnClickOutside={!ctrl.isAnyWriteInFlight}
        closeOnEscape={!ctrl.isAnyWriteInFlight}
      >
        <section className={shared.section}>
          <div className={shared.toolbar}>
            <Button type="button" variant="save" size="sm" onClick={() => setCreateModalOpen(true)} dataTestId="category-manager-create-open">
              צור קטגוריה
            </Button>
            <MantineButton
              type="button"
              variant={reorderMode ? 'filled' : 'default'}
              color="blue"
              radius="md"
              data-testid="category-manager-reorder-mode"
              aria-pressed={reorderMode}
              leftSection={<FontAwesomeIcon icon={faArrowsUpDown} />}
              onClick={() => setReorderMode((previous) => !previous)}
            >
              {reorderMode ? 'סיום מיון' : 'מיון'}
            </MantineButton>
          </div>

          {reorderMode && (
            <p className={productClasses.reorderHint} data-testid="category-manager-reorder-hint">
              גרור לפי הידית לשינוי סדר הקטגוריות
            </p>
          )}

          <div ref={tableScrollRef} className={`${shared.tableWrap} ${reorderMode ? '' : productClasses.virtualScrollWrap}`}>
            {reorderMode ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                {renderCategoryTable(true)}
              </DndContext>
            ) : (
              renderCategoryTable(false)
            )}
          </div>
        </section>
      </Modal>

      <Modal
        opened={isFormModalOpen}
        onClose={closeFormModal}
        onContextMenu={(event) => event.preventDefault()}
        title={isEditMode ? 'עריכת קטגוריה' : 'קטגוריה חדשה'}
        size="md"
        centered
        radius="md"
        classNames={{ root: navyModal.root }}
        closeButtonProps={{ disabled: ctrl.isAnyWriteInFlight, 'aria-label': 'סגור', 'data-testid': 'category-manager-form-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
        closeOnClickOutside={!ctrl.isAnyWriteInFlight}
        closeOnEscape={!ctrl.isAnyWriteInFlight}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void ctrl.submitForm();
          }}
          className={shared.section}
        >
          {ctrl.rows.map((row, index) => (
            <div key={row.rowKey} className={productClasses.productFormRow}>
              {index > 0 && (
                <div className={productClasses.productFormRowHeader}>
                  <ActionIcon
                    aria-label="הסר קטגוריה"
                    data-testid={`category-manager-form-remove-${index}`}
                    variant="subtle"
                    color="red"
                    radius="md"
                    size="sm"
                    disabled={ctrl.isAnyWriteInFlight}
                    onClick={() => ctrl.removeRow(index)}
                  >
                    <FontAwesomeIcon icon={faXmarkRemove} />
                  </ActionIcon>
                </div>
              )}
              <div className={shared.formRow}>
                <TextInput
                  data-testid={`category-manager-form-name-${index}`}
                  label="שם הקטגוריה"
                  placeholder="שם הקטגוריה"
                  value={row.name}
                  onChange={(event) => ctrl.updateRowField(index, 'name', event.currentTarget.value)}
                  minLength={1}
                  maxLength={120}
                  required
                  radius="md"
                />
                <Group gap="md">
                  <Checkbox
                    data-testid={`category-manager-form-food-${index}`}
                    label="אוכל"
                    checked={row.reportGroup === 'food'}
                    onChange={() => ctrl.updateRowField(index, 'reportGroup', row.reportGroup === 'food' ? null : 'food')}
                  />
                  <Checkbox
                    data-testid={`category-manager-form-beverages-${index}`}
                    label="משקאות"
                    checked={row.reportGroup === 'beverages'}
                    onChange={() => ctrl.updateRowField(index, 'reportGroup', row.reportGroup === 'beverages' ? null : 'beverages')}
                  />
                  <Checkbox
                    data-testid={`category-manager-form-separate-kitchen-bon-${index}`}
                    label="בון נפרד בהדפסה"
                    checked={row.separateKitchenBon}
                    onChange={(event) => ctrl.updateRowField(index, 'separateKitchenBon', event.currentTarget.checked)}
                  />
                </Group>
                <Select
                  data-testid="category-manager-form-parent"
                  label="קטגוריית אם"
                  placeholder="ללא (קטגוריה ראשית)"
                  clearable
                  radius="md"
                  data={ctrl.parentCategoryOptions}
                  value={row.parentCategoryPk === null ? null : String(row.parentCategoryPk)}
                  onChange={(value) => ctrl.updateRowField(index, 'parentCategoryPk', value === null ? null : Number(value))}
                  disabled={ctrl.isAnyWriteInFlight}
                />
                <QuickNotesField
                  value={row.quickNotes}
                  disabled={ctrl.isAnyWriteInFlight}
                  onChange={(next) => ctrl.updateRowField(index, 'quickNotes', next)}
                />
              </div>
            </div>
          ))}
          <div className={isEditMode ? productClasses.editFormFooter : productClasses.productFormFooter}>
            {isEditMode && editingCategory && (
              <Switch
                data-testid="category-edit-status"
                aria-label={editingCategory.isActive ? 'פעיל' : 'לא פעיל'}
                onLabel="פעיל"
                offLabel="לא פעיל"
                size="lg"
                checked={editingCategory.isActive}
                color="green"
                className={productClasses.statusSwitch}
                onChange={() => void ctrl.toggleActive(editingCategory)}
                disabled={ctrl.togglingId === editingCategory.id || ctrl.isAnyWriteInFlight}
              />
            )}
            {!isEditMode && (
              <MantineButton type="button" variant="default" radius="md" className={productClasses.addRowButton} data-testid="category-manager-form-add-row" onClick={ctrl.addRow} disabled={ctrl.isAnyWriteInFlight}>
                הוסף קטגוריה נוספת
              </MantineButton>
            )}
            <Button
              type="submit"
              variant="save"
              disabled={!ctrl.rows[0].name.trim() || ctrl.isAnyWriteInFlight}
              loading={ctrl.isSavingForm}
              loadingLabel="שומר..."
              dataTestId="category-manager-form-submit"
            >
              שמור
            </Button>
          </div>
        </form>
      </Modal>

      <CategoryMembershipDrawer
        category={ctrl.membershipCategory}
        activeProducts={ctrl.activeProducts}
        categoryById={ctrl.categoryById}
        checkedProductIds={ctrl.checkedProductIds}
        isSaving={ctrl.isAnyWriteInFlight}
        onToggleProduct={ctrl.toggleProduct}
        onSave={() => void ctrl.saveMembership()}
        onClose={() => ctrl.setMembershipCategoryId(null)}
      />
    </>
  );
};

export const CategoryManagerModal = React.memo(CategoryManagerModalComponent);
